import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// WC claim readiness (billing PR #5, 2026-10-29) - SHADOW and DARK.
// Two views on one page, behind the server's billing gate like the rest of Billing
// (anyone else is sent home as if the page didn't exist):
//   /billing/wc                 the checks: WC claims (case + date of service) in a window that
//                               would bounce or go on paper, and the review holds
//   /billing/wc/check/:check    the claims one check flags, oldest first
// The window lives in the URL as ?window=30|90|365&by=dos|entered&open=1 - never a patient,
// case or claim number. Nothing here changes Raintree or anything else: reads only.

const PAGE = 100;
const WINDOWS = ["30", "90", "365"];

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function readFilters(search) {
  const q = new URLSearchParams(search);
  const win = WINDOWS.includes(q.get("window")) ? q.get("window") : "30";
  const by = q.get("by") === "entered" ? "entered" : "dos";
  const open = q.get("open") === "1" ? "1" : "0";
  return { window: win, by, open };
}

function filterSearch({ window: win, by, open }) {
  const q = new URLSearchParams();
  if (win !== "30") q.set("window", win);
  if (by !== "dos") q.set("by", by);
  if (open === "1") q.set("open", "1");
  const s = q.toString();
  return s ? `?${s}` : "";
}

function Note({ children, tone = "slate" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600";
  return <div className={`mt-4 rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function Filters({ filters, base }) {
  const navigate = useNavigate();
  const go = (patch) => navigate({ pathname: base, search: filterSearch({ ...filters, ...patch }) });
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="wc-filters">
      <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {WINDOWS.map((w) => (
          <button key={w} onClick={() => go({ window: w })} data-testid={`wc-window-${w}`}
                  className={`rounded-md px-3 py-1 ${filters.window === w ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            Last {w} days
          </button>
        ))}
      </div>
      <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {[["dos", "By date of service"], ["entered", "By date entered"]].map(([k, label]) => (
          <button key={k} onClick={() => go({ by: k })} data-testid={`wc-by-${k}`}
                  className={`rounded-md px-3 py-1 ${filters.by === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {label}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-slate-600">
        <input type="checkbox" checked={filters.open === "1"} onChange={(e) => go({ open: e.target.checked ? "1" : "0" })}
               data-testid="wc-open" />
        Open balance only
      </label>
    </div>
  );
}

// ── the checks ────────────────────────────────────────────────────────

function Checks({ filters }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { window: win, by, open } = filters;

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api
      .get("/billing/wc/readiness", { params: { window: win, by, open } })
      .then((res) => live && setData(res.data))
      .catch(() => live && setError("Couldn't load the WC claim checks."));
    return () => {
      live = false;
    };
  }, [win, by, open]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const [bounce, review] = data.groups;
  const byLabel = data.by === "entered" ? "entered in Raintree" : "by date of service";
  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="WC claims" value={data.claims.toLocaleString()} sub={`last ${data.window} days, ${byLabel}`} testid="wc-claims" />
        <Stat label={bounce.label} value={bounce.claims.toLocaleString()} sub="claims with at least one" testid="wc-bounce" />
        <Stat label={review.label} value={review.claims.toLocaleString()} sub="claims with at least one" testid="wc-review" />
        <Stat label="Clean" value={data.clean.toLocaleString()} sub="no check flags them" testid="wc-clean" />
      </div>
      {data.groups.map((g) => (
        <div key={g.key} className="mt-6">
          <h2 className="text-base font-semibold text-slate-800">{g.label}</h2>
          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm" data-testid={`wc-group-${g.key}`}>
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Check</th>
                  <th className="px-4 py-2 text-right">Claims</th>
                  <th className="px-4 py-2 text-right">Billed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {g.checks.map((c) => (
                  <tr key={c.key}>
                    <td className="px-4 py-2">
                      {c.claims > 0 ? (
                        <Link to={{ pathname: `/billing/wc/check/${c.key}`, search: filterSearch(filters) }}
                              className="text-blue-600 hover:underline" data-testid={`wc-check-${c.key}`}>
                          {c.label}
                        </Link>
                      ) : (
                        <span className="text-slate-500">{c.label}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">{c.claims.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{money(c.billed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <p className="mt-4 text-xs text-slate-500" data-testid="wc-as-of">
        Today (Pacific) {usDate(data.today)}. Raintree mirror last refreshed {data.as_of ? new Date(data.as_of).toLocaleString() : "—"}.
        A claim can show under more than one check.
      </p>
    </>
  );
}

// ── one check's claims ────────────────────────────────────────────────

function CheckClaims({ check, filters }) {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { window: win, by, open } = filters;

  useEffect(() => {
    setOffset(0);
  }, [check, win, by, open]);

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api
      .get(`/billing/wc/readiness/${check}`, { params: { window: win, by, open, offset, limit: PAGE } })
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 404 ? "There's no such check." : "Couldn't load these claims."));
    return () => {
      live = false;
    };
  }, [check, offset, win, by, open]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  return (
    <>
      <h2 className="mt-4 text-base font-semibold text-slate-800" data-testid="wc-check-title">
        {data.label}: {data.total.toLocaleString()} claim{data.total === 1 ? "" : "s"}
      </h2>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="wc-claims-list">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Patient</th>
              <th className="px-3 py-2">Case</th>
              <th className="px-3 py-2">Date of service</th>
              <th className="px-3 py-2">Entered</th>
              <th className="px-3 py-2">Carrier</th>
              <th className="px-3 py-2">Claim #</th>
              <th className="px-3 py-2 text-right">Charges</th>
              <th className="px-3 py-2 text-right">Billed</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-right">Days left to bill</th>
              <th className="px-3 py-2">Also flagged</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.claims.map((c) => (
              <tr key={`${c.case_id}-${c.date_of_service}`}>
                <td className="px-3 py-1.5">
                  <Link to={`/billing/case/${c.case_id}`} className="font-medium text-blue-700 hover:underline">
                    {c.patient_name || "—"}
                  </Link>
                  <div className="text-xs text-slate-400">{c.patient_pn || ""}</div>
                </td>
                <td className="px-3 py-1.5">{c.case_number || "—"}</td>
                <td className="px-3 py-1.5">{usDate(c.date_of_service)}</td>
                <td className="px-3 py-1.5">{usDate(c.entered)}</td>
                <td className="px-3 py-1.5">{c.payor_name || "—"}</td>
                <td className="px-3 py-1.5">{c.claim_number || "—"}</td>
                <td className="px-3 py-1.5 text-right">{c.charges}</td>
                <td className="px-3 py-1.5 text-right">{money(c.billed)}</td>
                <td className="px-3 py-1.5 text-right">{money(c.balance)}</td>
                <td className={`px-3 py-1.5 text-right ${c.deadline_days < 30 ? "font-medium text-rose-700" : ""}`}>
                  {c.deadline_days < 0 ? "past" : c.deadline_days}
                </td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{c.flags.filter((f) => f !== check).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm">
        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Previous</button>
        <span className="text-slate-500">
          {data.total === 0 ? "0" : `${offset + 1}-${Math.min(offset + PAGE, data.total)}`} of {data.total.toLocaleString()}
        </span>
        <button disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Next</button>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Oldest first. Days left to bill = 365 minus days since the date of service (California WC bills are due within 12
        months of the date of service).
      </p>
    </>
  );
}

export default function BillingWC() {
  const { user } = useAuth();
  const { check } = useParams();
  const location = useLocation();
  const [allowed, setAllowed] = useState(null);
  const filters = readFilters(location.search);

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="p-8 max-w-7xl" data-testid="billing-wc">
      <BillingNav current="wc" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>
        {" / "}
        {check ? <Link to={{ pathname: "/billing/wc", search: filterSearch(filters) }} className="text-sky-700 hover:underline">WC claim readiness</Link> : "WC claim readiness"}
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">WC claim readiness</h1>
        <Filters filters={filters} base={location.pathname} />
      </div>
      <p className="mt-1 text-sm text-slate-500" data-testid="billing-wc-shadow-note">
        Shadow mode. Read from the Raintree mirror; nothing here changes Raintree or sends anything.
      </p>
      {check ? <CheckClaims check={check} filters={filters} /> : <Checks filters={filters} />}
    </div>
  );
}
